import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Radio, Plus, PowerOff, Power, Trash2, Copy, Loader2, Code,
  Youtube, Twitch, Globe, AlertTriangle, Target, Edit3,
} from "lucide-react";
import { toast } from "sonner";

const STREAM_TYPES = [
  { value: "youtube", label: "YouTube", icon: Youtube },
  { value: "twitch", label: "Twitch", icon: Twitch },
  { value: "hls", label: "HLS (.m3u8)", icon: Radio },
  { value: "rtmp", label: "RTMP (no embed)", icon: Radio },
  { value: "custom_embed", label: "Custom Embed URL", icon: Code },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function snippetHTML(hullKey) {
  return `<!-- Armada Stream Ribbon · paste into any hull's <body> -->
<script>
(function(){
  var hull = ${JSON.stringify(hullKey)};
  var api = ${JSON.stringify(BACKEND)};
  function tick(){
    fetch(api + "/api/broadcast/active/" + hull).then(function(r){return r.json();}).then(function(d){
      var el = document.getElementById("armada-stream-ribbon");
      if (d.active) {
        var b = d.broadcast;
        if (!el) {
          el = document.createElement("div");
          el.id = "armada-stream-ribbon";
          el.style.cssText = "position:fixed;top:0;left:0;right:0;z-index:9999;background:#0A0A0A;color:#fff;padding:10px 16px;border-bottom:1px solid #C9A227;font-family:Montserrat,system-ui,sans-serif;font-size:13px;display:flex;align-items:center;gap:10px;";
          document.body.prepend(el);
        }
        el.innerHTML = '<span style="display:inline-block;width:8px;height:8px;background:#ef4444;border-radius:50%;animation:armadaPulse 1.5s infinite"></span> <strong style="color:#C9A227;letter-spacing:.1em;text-transform:uppercase">LIVE</strong> <span>' + b.title + '</span> <a href="' + b.stream_url + '" target="_blank" rel="noopener" style="margin-left:auto;color:#C9A227;text-decoration:none;font-weight:700">Watch \\u2192</a>';
      } else if (el) {
        el.remove();
      }
    }).catch(function(){});
  }
  var s = document.createElement("style"); s.innerText="@keyframes armadaPulse{0%,100%{opacity:1}50%{opacity:.3}}"; document.head.appendChild(s);
  tick(); setInterval(tick, 30000);
})();
</script>`;
}

function snippetReact(hullKey) {
  return `// ArmadaStreamRibbon.jsx — drop into the hull's app shell
import { useEffect, useState } from "react";

export default function ArmadaStreamRibbon() {
  const hull = ${JSON.stringify(hullKey)};
  const api = ${JSON.stringify(BACKEND)};
  const [b, setB] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = () =>
      fetch(api + "/api/broadcast/active/" + hull)
        .then(r => r.json())
        .then(d => { if (alive) setB(d.active ? d.broadcast : null); })
        .catch(() => {});
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!b) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-50 bg-black text-white border-b border-yellow-500 px-4 py-2.5 flex items-center gap-3 text-sm">
      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
      <strong className="text-yellow-400 uppercase tracking-widest text-xs">Live</strong>
      <span>{b.title}</span>
      <a href={b.stream_url} target="_blank" rel="noopener noreferrer"
         className="ml-auto text-yellow-400 font-bold">Watch \u2192</a>
    </div>
  );
}`;
}

function StreamForm({ initial, onSave, onCancel, fleetApps, saving }) {
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    stream_url: initial?.stream_url || "",
    stream_type: initial?.stream_type || "youtube",
    description: initial?.description || "",
    target_mode: initial?.target_mode || "all",
    target_categories: initial?.target_categories || [],
    target_hull_ids: initial?.target_hull_ids || [],
  }));

  const categories = useMemo(() => {
    const set = new Set();
    fleetApps.forEach((a) => set.add(a.category));
    return Array.from(set).sort();
  }, [fleetApps]);

  const toggleArr = (key, value) => {
    setForm((prev) => {
      const arr = prev[key].includes(value)
        ? prev[key].filter((x) => x !== value)
        : [...prev[key], value];
      return { ...prev, [key]: arr };
    });
  };

  return (
    <div className="card-sharp p-6 space-y-5" data-testid="stream-form">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-tag gold-text">{initial ? "Edit Broadcast" : "New Broadcast"}</div>
          <h3 className="text-2xl font-black tracking-tighter mt-1">Sovereign Stream</h3>
        </div>
        <button onClick={onCancel} className="text-xs label-tag hover:text-white" data-testid="stream-form-cancel">
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="label-tag mb-2">Title *</div>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Armada Quarterly Address"
            className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
            data-testid="stream-form-title"
          />
        </div>
        <div>
          <div className="label-tag mb-2">Stream Type</div>
          <select
            value={form.stream_type}
            onChange={(e) => setForm({ ...form, stream_type: e.target.value })}
            className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
            data-testid="stream-form-type"
          >
            {STREAM_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="label-tag mb-2">Stream URL *</div>
        <input
          value={form.stream_url}
          onChange={(e) => setForm({ ...form, stream_url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=..."
          className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none font-mono"
          data-testid="stream-form-url"
        />
      </div>

      <div>
        <div className="label-tag mb-2">Description</div>
        <textarea
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
          data-testid="stream-form-desc"
        />
      </div>

      <div className="space-y-3 border-t border-white/10 pt-5">
        <div className="label-tag flex items-center gap-2"><Target className="w-3 h-3" /> Broadcast Target</div>
        <div className="flex gap-2">
          {["all", "category", "specific"].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setForm({ ...form, target_mode: mode })}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold border ${
                form.target_mode === mode
                  ? "border-[#C9A227] gold-text bg-[#C9A227]/5"
                  : "border-white/10 text-gray-400 hover:border-white/30"
              }`}
              data-testid={`stream-target-${mode}`}
            >
              {mode === "all" ? "All Hulls" : mode === "category" ? "By Category" : "Specific Hulls"}
            </button>
          ))}
        </div>

        {form.target_mode === "category" && (
          <div className="flex flex-wrap gap-2 pt-2" data-testid="stream-target-categories">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggleArr("target_categories", c)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold border ${
                  form.target_categories.includes(c)
                    ? "border-[#C9A227] gold-text bg-[#C9A227]/5"
                    : "border-white/10 text-gray-400 hover:border-white/30"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {form.target_mode === "specific" && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-3 border border-white/10" data-testid="stream-target-hulls">
            {fleetApps.map((h) => (
              <label key={h.id} className="flex items-center gap-2 text-xs cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={form.target_hull_ids.includes(h.id)}
                  onChange={() => toggleArr("target_hull_ids", h.id)}
                />
                <span className="truncate">{h.display_name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
        <button onClick={onCancel} className="text-xs label-tag hover:text-white">Cancel</button>
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.title || !form.stream_url}
          className="btn-gold inline-flex items-center gap-2"
          data-testid="stream-form-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
          {initial ? "Save Broadcast" : "Create Broadcast"}
        </button>
      </div>
    </div>
  );
}

function EmbedDialog({ broadcast, onClose, fleetApps }) {
  const [tab, setTab] = useState("html");
  const [hullPick, setHullPick] = useState(fleetApps[0]?.name || "");
  if (!broadcast) return null;

  const code = tab === "html" ? snippetHTML(hullPick) : snippetReact(hullPick);

  const copy = () => {
    navigator.clipboard.writeText(code);
    toast.success("Snippet copied", { description: `Paste into the ${hullPick} hull.` });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="embed-dialog">
      <div className="glass-strong w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="label-tag gold-text">Embed Snippet</div>
            <h3 className="text-xl font-black tracking-tighter mt-1">{broadcast.title}</h3>
            <p className="text-xs text-gray-400 mt-1">
              Paste this <strong>once per hull</strong>. After install, every armed broadcast
              targeting that hull appears within 30 seconds. No re-deploy needed thereafter.
            </p>
          </div>
          <button onClick={onClose} className="text-xs label-tag hover:text-white" data-testid="embed-dialog-close">Close</button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex">
            <button onClick={() => setTab("html")}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${tab==="html"?"border-[#C9A227] gold-text bg-[#C9A227]/5":"border-white/10 text-gray-400"}`}
              data-testid="embed-tab-html">HTML / Any Stack</button>
            <button onClick={() => setTab("react")}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border-y border-r ${tab==="react"?"border-[#C9A227] gold-text bg-[#C9A227]/5":"border-white/10 text-gray-400"}`}
              data-testid="embed-tab-react">React Component</button>
          </div>
          <select
            value={hullPick}
            onChange={(e) => setHullPick(e.target.value)}
            className="bg-black/50 border border-white/10 px-3 py-1.5 text-xs text-white focus:border-[#C9A227] focus:outline-none rounded-none"
            data-testid="embed-hull-pick"
          >
            {fleetApps.map((h) => <option key={h.id} value={h.name}>{h.display_name}</option>)}
          </select>
          <button onClick={copy} className="btn-gold inline-flex items-center gap-2 text-xs" data-testid="embed-copy">
            <Copy className="w-3.5 h-3.5" /> Copy Snippet
          </button>
        </div>

        <pre className="bg-black/70 border border-white/10 p-4 text-[11px] font-mono text-gray-300 overflow-x-auto leading-relaxed whitespace-pre"
             data-testid="embed-code">{code}</pre>

        <div className="border border-yellow-500/30 bg-yellow-500/5 p-3 text-xs text-yellow-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Manual integration step.</strong> Each hull needs this snippet pasted once.
            The hosting platform's deploy hooks only run in dev; this code-push pattern isn't available cross-app. I can't do this for you.
            Once installed, real-time broadcast control from this console works as expected.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Stream() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [fleetApps, setFleetApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [embedFor, setEmbedFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, f] = await Promise.all([api.listBroadcasts(), api.listFleetApps()]);
      setBroadcasts(b);
      setFleetApps(f);
    } catch (err) {
      toast.error("Failed to load Stream Controller", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        const updated = await api.updateBroadcast(editing.id, form);
        setBroadcasts((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
        toast.success("Broadcast updated");
      } else {
        const created = await api.createBroadcast(form);
        setBroadcasts((prev) => [created, ...prev]);
        toast.success("Broadcast created", { description: "Arm it to go live." });
      }
      setShowForm(false);
      setEditing(null);
    } catch (err) {
      toast.error("Save failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleArm = async (b) => {
    try {
      const updated = await api.updateBroadcast(b.id, { armed: !b.armed });
      setBroadcasts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      toast.success(updated.armed ? "Broadcast ARMED · going live" : "Broadcast DISARMED");
    } catch (err) {
      toast.error("Failed", { description: err.message });
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete broadcast "${b.title}"?`)) return;
    try {
      await api.deleteBroadcast(b.id);
      setBroadcasts((prev) => prev.filter((x) => x.id !== b.id));
      toast.success("Broadcast deleted");
    } catch (err) {
      toast.error("Delete failed", { description: err.message });
    }
  };

  const armedCount = broadcasts.filter((b) => b.armed).length;

  return (
    <div className="space-y-10" data-testid="stream-page">
      <section>
        <div className="flex items-center gap-3 mb-3">
          <Radio className="w-5 h-5 gold-text" strokeWidth={1.5} />
          <span className="label-tag">Sovereign Stream Controller</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
          One Signal. <span className="gold-text">Many Hulls.</span>
        </h1>
        <p className="text-gray-400 mt-3 max-w-3xl">
          Configure a live broadcast once. Choose who receives it: the entire Armada, a category,
          or hand-picked hulls. Arm to go live. Subscribed hulls display the LIVE ribbon within 30 seconds.
        </p>
      </section>

      <div className="glass p-4 flex items-start gap-3 text-sm" data-testid="stream-pull-notice">
        <AlertTriangle className="w-4 h-4 gold-text flex-shrink-0 mt-0.5" strokeWidth={1.8} />
        <div className="text-gray-300 leading-relaxed">
          <strong className="text-white">Pull pattern:</strong> each target hull needs the
          embed snippet pasted once (use the <strong>Embed</strong> button below). After that,
          every armed broadcast from this console reaches the hull automatically. There is no
          cross-app code-push API in the hosting platform — this is the legitimate way to do fleet-wide live.
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-sharp p-4" data-testid="stream-stat-total">
          <div className="label-tag">Total Broadcasts</div>
          <div className="text-3xl font-black mt-1">{broadcasts.length}</div>
        </div>
        <div className="card-sharp p-4" data-testid="stream-stat-armed">
          <div className="label-tag">Armed (Live)</div>
          <div className={`text-3xl font-black mt-1 ${armedCount > 0 ? "text-emerald-400" : "text-gray-500"}`}>
            {armedCount}
          </div>
        </div>
        <div className="card-sharp p-4" data-testid="stream-stat-fleet">
          <div className="label-tag">Subscribable Hulls</div>
          <div className="text-3xl font-black mt-1 gold-text">{fleetApps.length}</div>
        </div>
        <div className="card-sharp p-4 flex items-center justify-center">
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="btn-gold inline-flex items-center gap-2 w-full justify-center"
            data-testid="stream-new-btn"
          >
            <Plus className="w-4 h-4" /> New Broadcast
          </button>
        </div>
      </section>

      {showForm && (
        <StreamForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          fleetApps={fleetApps}
          saving={saving}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-500" data-testid="stream-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Tuning the signal…
        </div>
      ) : broadcasts.length === 0 ? (
        <div className="card-sharp p-12 text-center" data-testid="stream-empty">
          <Radio className="w-10 h-10 gold-text mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-gray-400">No broadcasts yet. Create one to begin.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="broadcast-list">
          {broadcasts.map((b) => (
            <div key={b.id} className={`card-sharp p-6 ${b.armed ? "border-emerald-500/40 gold-glow" : ""}`} data-testid={`broadcast-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-3">
                    {b.armed && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-400 border border-emerald-500/40 px-2 py-0.5">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE
                      </span>
                    )}
                    <span className="label-tag uppercase">{b.stream_type}</span>
                    <span className="label-tag">
                      {b.target_mode === "all" ? "→ All Hulls" :
                       b.target_mode === "category" ? `→ ${b.target_categories.length} categor${b.target_categories.length===1?"y":"ies"}` :
                       `→ ${b.target_hull_ids.length} specific`}
                    </span>
                  </div>
                  <h3 className="text-2xl font-black tracking-tighter mt-2">{b.title}</h3>
                  {b.description && <p className="text-sm text-gray-400 mt-1">{b.description}</p>}
                  <a href={b.stream_url} target="_blank" rel="noopener noreferrer"
                     className="text-xs font-mono gold-text inline-flex items-center gap-1 mt-2 hover:text-[#E0BC4A]">
                    <Globe className="w-3 h-3" /> {b.stream_url.slice(0, 70)}{b.stream_url.length > 70 ? "…" : ""}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleArm(b)}
                    className={b.armed ? "btn-ghost-gold text-xs inline-flex items-center gap-2"
                                       : "btn-gold text-xs inline-flex items-center gap-2"}
                    data-testid={`broadcast-arm-${b.id}`}
                  >
                    {b.armed ? <><PowerOff className="w-3.5 h-3.5" /> Disarm</>
                             : <><Power className="w-3.5 h-3.5" /> Arm</>}
                  </button>
                  <button
                    onClick={() => setEmbedFor(b)}
                    className="text-xs label-tag border border-white/10 hover:border-[#C9A227]/40 hover:gold-text px-3 py-2 inline-flex items-center gap-1.5"
                    data-testid={`broadcast-embed-${b.id}`}
                  >
                    <Code className="w-3 h-3" /> Embed
                  </button>
                  <button
                    onClick={() => { setEditing(b); setShowForm(true); }}
                    className="text-xs label-tag border border-white/10 hover:border-white/30 px-3 py-2 inline-flex items-center gap-1.5"
                    data-testid={`broadcast-edit-${b.id}`}
                  >
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => remove(b)}
                    className="text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10 px-3 py-2 inline-flex items-center gap-1.5"
                    data-testid={`broadcast-delete-${b.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {b.armed && b.embed_url && (
                <div className="mt-5 aspect-video w-full bg-black border border-[#C9A227]/20 overflow-hidden">
                  <iframe
                    src={b.embed_url}
                    title={b.title}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              )}
              {b.armed && !b.embed_url && (
                <div className="mt-5 p-4 border border-yellow-500/30 bg-yellow-500/5 text-xs text-yellow-200">
                  {b.stream_type === "rtmp"
                    ? "RTMP streams cannot be embedded directly. Subscribers will see the LIVE ribbon and a link to the stream URL."
                    : "Embed URL could not be derived. Subscribers see the LIVE ribbon + link only."}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {embedFor && (
        <EmbedDialog broadcast={embedFor} onClose={() => setEmbedFor(null)} fleetApps={fleetApps} />
      )}
    </div>
  );
}
